import dayjs from "dayjs";
import { computeProfileMetrics } from "./insightsLogicRegistry";

/**
 * Formats a percentage value for display.
 */
const formatPercent = (num, includeSign = false) => {
  if (isNaN(num)) return "N/A";
  const sign = num > 0 && includeSign ? "+" : "";
  return `${sign}${num.toFixed(2)}%`;
};

/**
 * Formats a content type to its plural form.
 */
const pluralizeFormat = (type) => {
  const t = (type || "").toLowerCase();
  if (t === "status") return "statuses";
  if (t === "n/a" || t === "content") return "content";
  if (t.endsWith("s")) return t;
  return `${t}s`;
};

/**
 * Normalizes a brand name to its root form for cross-platform matching.
 * Strips punctuation, whitespace, and platform-specific suffixes (Official, Verified, Fanpage).
 */
const normalizeBrandName = (name) => {
  if (!name) return "";
  return name.toLowerCase()
    .replace(/[^\w\s]/g, "") // Remove punctuation
    .replace(/\b(official|verified|fanpage|inc|ltd|corp|fans|page|social|media|channel)\b/g, "") // Remove noise
    .replace(/\s+/g, "") // Remove all whitespace
    .trim();
};

/**
 * 🆕 Generates the dynamic narrative bullet points with Strategy Gaps.
 * Uses mirrored logic from KeyMetrics to ensure 100% data parity.
 */
export const generateStrategicNarrative = (datasets, previousdatasets = [], adjustedcurrentdatasets = [], adjustedpreviousdatasets = []) => {
  if (!datasets || datasets.length === 0) return [];

  // 1. Pre-compute metrics for all profiles using the "Zero-Touch" replicated logic
  const allProfilesData = datasets.map(p => 
    computeProfileMetrics(p, datasets, previousdatasets, adjustedcurrentdatasets, adjustedpreviousdatasets)
  );

  const primaryBrand = allProfilesData.find(p => p.isPrimary) || allProfilesData[0];
  const { audience, engagement, content, brandName, platform: rawPlatform } = primaryBrand;
  const platform = rawPlatform ? rawPlatform.charAt(0).toUpperCase() + rawPlatform.slice(1).toLowerCase() : "Facebook";
  let bulletPoints = [];

  const hasCompetitors = allProfilesData.length > 1;

  // 1. Detect Cross-Platform "Same Brand" Search using Fuzzy Normalization
  const uniqueBrands = new Set(allProfilesData.filter(p => p.brandName).map(p => normalizeBrandName(p.brandName)));
  const uniquePlatforms = new Set(allProfilesData.filter(p => p.platform).map(p => p.platform.toLowerCase()));
  const isCrossPlatformAudit = uniqueBrands.size === 1 && uniquePlatforms.size > 1;

  if (isCrossPlatformAudit) {
    // --- CROSS-PLATFORM SYNERGY LOGIC ---
    const sortedByResonance = [...allProfilesData].sort((a, b) => (b.engagement?.avgEngagement || 0) - (a.engagement?.avgEngagement || 0));
    const leader = sortedByResonance[0];
    const lName = leader.brandName;
    const lPlatform = (leader.platform || "Platform").charAt(0).toUpperCase() + (leader.platform || "Platform").slice(1).toLowerCase();
    
    // Insight 1: Platform Resonance Skew
    const rest = sortedByResonance.slice(1);
    const avgResonanceRest = rest.reduce((acc, p) => acc + (p.engagement?.avgEngagement || 0), 0) / (rest.length || 1);
    const skewFactor = leader.engagement?.avgEngagement / (avgResonanceRest || 1);

    bulletPoints.push({
      category: "Platform Synergy",
      type: "SYNERGY",
      description: `${lName}'s ${lPlatform} presence is currently the 'Resonance Anchor' for the brand, achieving ${skewFactor.toFixed(1)}x higher impact per post than other networks.`,
      targetAction: `Analyze the creative hooks of the top ${lPlatform} content and port these 'High-Resonance' elements to secondary channels to improve cross-platform ROI.`,
      icon: "hub",
      color: "success"
    });

    // Insight 2: Resource Efficiency Gap (Volume vs ROI)
    const sortedByVolume = [...allProfilesData].sort((a, b) => (b.content?.totalPublished || 0) - (a.content?.totalPublished || 0));
    const highVolume = sortedByVolume[0];
    const lowVolume = sortedByVolume.at(-1);

    if (highVolume.engagement?.avgEngagement < lowVolume.engagement?.avgEngagement * 0.5) {
      bulletPoints.push({
        category: "Efficiency Skew",
        type: "EFFICIENCY",
        description: `The ${highVolume.platform.charAt(0).toUpperCase() + highVolume.platform.slice(1).toLowerCase()} channel for ${highVolume.brandName} has the highest volume (${highVolume.content?.totalPublished} posts) but produces the lowest resonance return per piece.`,
        targetAction: `Reduce ${highVolume.platform.charAt(0).toUpperCase() + highVolume.platform.slice(1).toLowerCase()} production overhead by 25% and reallocate those resources to ${lowVolume.platform.charAt(0).toUpperCase() + lowVolume.platform.slice(1).toLowerCase()} where the current ROI is significantly higher.`,
        icon: "balance",
        color: "warning"
      });
    }

    // Insight 3: Network Health Verdict
    const sortedByGrowth = [...allProfilesData].sort((a, b) => (b.audience?.growthPct || 0) - (a.audience?.growthPct || 0));
    const growthLeader = sortedByGrowth[0];
    const growthLaggard = sortedByGrowth.at(-1);
    const glPlatform = growthLeader.platform.charAt(0).toUpperCase() + growthLeader.platform.slice(1).toLowerCase();
    const gLagPlatform = growthLaggard.platform.charAt(0).toUpperCase() + growthLaggard.platform.slice(1).toLowerCase();

    if (growthLeader.audience?.growthPct > 0.05) {
      bulletPoints.push({
        category: "Portfolio Dynamics",
        type: "DYNAMICS",
        description: `Audience sentiment is currently shifting toward ${growthLeader.brandName}'s ${glPlatform} presence (+${growthLeader.audience?.growthPct.toFixed(2)}%), while ${gLagPlatform} is entering a consolidation phase.`,
        targetAction: `Leverage ${growthLeader.brandName}'s ${glPlatform} momentum to cross-promote the ${gLagPlatform} profile using 'Exclusive Series' or 'BTS' content to stabilize the broader audience pool.`,
        icon: "trending_up",
        color: "info"
      });
    }
  } else if (hasCompetitors) {
    // 1. Establish Performance Benchmark (Who is the "North Star" in this set?)
    let overallLeaderEngagement = -Infinity;
    let overallLeaderType = "mixed content";
    let overallLeaderName = "market leader";
    let leaderTopPost = null;
    
    allProfilesData.forEach(p => {
      const engVolume = p.engagement?.avgEngagement || 0;
      if (engVolume > overallLeaderEngagement) {
        overallLeaderEngagement = engVolume;
        overallLeaderType = (p.content?.topType || "content").toLowerCase();
        overallLeaderName = p.brandName;
        leaderTopPost = (p.content?.impactPosts || []).find(post => post.relativePerformance > 1.2) || (p.content?.impactPosts || [])[0];
      }
    });

    const competitors = allProfilesData.filter(p => !p.brandName || p.brandName !== brandName);
    let highestCompEngVolume = -Infinity;
    let highestCompEngRate = -Infinity;
    let engagementCompetitorLeader = "the market leader";
    let highestCompGrowthRate = -Infinity;
    let growthCompetitorLeader = "the market leader";

    competitors.forEach(p => {
      const engVolume = p.engagement?.avgEngagement || 0;
      if (engVolume > highestCompEngVolume) {
        highestCompEngVolume = engVolume;
        engagementCompetitorLeader = p.brandName;
      }
      const engRate = p.engagement?.rateAvg || 0;
      if (engRate > highestCompEngRate) {
        highestCompEngRate = engRate;
      }
      const growthRate = p.audience?.growthPct || 0;
      if (growthRate > highestCompGrowthRate) {
        highestCompGrowthRate = growthRate;
        growthCompetitorLeader = p.brandName;
      }
    });

    const marketAvgPosts = competitors.reduce((acc, p) => acc + (p.content?.totalPublished || 0), 0) / (competitors.length || 1);
    const primaryTopType = (content.topType || "content").toLowerCase();
    const isPrimaryLeader = brandName === overallLeaderName;

    // Check for "Internal Performance Outlier" (Is their current format winning internally?)
    const internalDriver = (content.impactPosts || []).find(p => p.type.toLowerCase() === primaryTopType && p.relativePerformance > 1.2);
    const isInternallyWinning = !!internalDriver;

    // Insight 1: Strategic Format Comparison
    if (!isPrimaryLeader && primaryTopType !== overallLeaderType && overallLeaderType !== "mixed content") {
      const pPlural = pluralizeFormat(primaryTopType);
      const cPlural = pluralizeFormat(overallLeaderType);
      
      const leaderPostContext = leaderTopPost ? ` (anchored by their **${leaderTopPost.type}** "${leaderTopPost.title.substring(0, 25)}...")` : "";

      if (isInternallyWinning) {
        // Balanced Advice: Internal Success > External Benchmark
        const perfBoost = Math.round((internalDriver.relativePerformance - 1) * 100);
        bulletPoints.push({
          category: "Opportunistic Expansion",
          type: "EXPANSION",
          description: `While the ${platform} leader (${overallLeaderName}) wins with ${cPlural}${leaderPostContext}, ${brandName}'s ${pPlural} are an internal outlier (+${perfBoost}% ROI).`,
          targetAction: `Maintain the high-performing ${primaryTopType} strategy while cautiously testing ${overallLeaderType} to capture secondary reach.`,
          icon: "add_chart",
          color: "info"
        });
      } else {
        // Hard Pivot: Behind the market leader
        bulletPoints.push({
          category: "Strategic Format Pivot",
          type: "GAP",
          description: `While ${brandName} focuses on ${pPlural}, the ${platform} performance leader (${overallLeaderName}) is winning with ${cPlural}${leaderPostContext}.`,
          targetAction: `Redirect 50% of the ${primaryTopType} production budget to ${overallLeaderType} to close this high-impact resonance gap.`,
          icon: "transform",
          color: "warning"
        });
      }
    } else {
      const pPlural = pluralizeFormat(primaryTopType);
      const isLeader = engagement.avgEngagement >= highestCompEngVolume;
      const leaderStatus = isLeader ? "the market consensus" : "your top-performing competitors";
      const leaderPostContext = (!isLeader && leaderTopPost) ? ` which is anchored by **${overallLeaderName}'s ${leaderTopPost.type}** content` : "";
      
      const targetAction = isLeader ? `Defend market position by auditing top-performing ${pPlural}` : `Benchmark the next 3 posts against the resonance leader (${engagementCompetitorLeader})`;

      bulletPoints.push({
        category: "Format Alignment",
        type: "ALIGNMENT",
        description: `${brandName} is currently aligned with ${leaderStatus} by prioritizing ${pPlural} on ${platform}${leaderPostContext}.`,
        targetAction: `${targetAction} to capture additional market share.`,
        icon: "check_circle",
        color: "success"
      });
    }

    // Insight 2: Audience Health & Attribution (Always prioritized if growth exists)
    const growthPct = audience.growthPct || 0;
    const isGrowthLeader = growthPct >= highestCompGrowthRate;
    const growthVerdict = growthPct > 0.5 ? "expanded significantly" : (growthPct > 0 ? "grew by " + formatPercent(growthPct, true) : (growthPct < -0.1 ? "declined" : "remained stable"));
    const growthLeaderText = !isGrowthLeader ? `, in a market led by ${growthCompetitorLeader}` : "";
    
    let attribution = "";
    let audienceTargetAction = "Maintain this content pillar to defend your position";
    
    if (growthPct > 0.1 && content.impactPosts?.length > 0) {
       const topPost = content.impactPosts
         .filter(p => p.change > 0 && p.relativePerformance > 1.2)
         .sort((a, b) => b.relativePerformance - a.relativePerformance)[0];
       if (topPost) {
         const perfBoost = Math.round((topPost.relativePerformance - 1) * 100);
         const titleContext = topPost.title && topPost.title !== "unnamed content" ? ` ("${topPost.title.substring(0, 30)}...")` : "";
         attribution = `. This trend was fueled by ${brandName}'s **${topPost.type}** content${titleContext} which outperformed the baseline by ${perfBoost}%`;
         audienceTargetAction = `Double-down on the **${topPost.type}** format as the primary growth driver for the next 14 days`;
       }
    } else if (isGrowthLeader) {
      audienceTargetAction = `Defend growth leadership against ${growthCompetitorLeader}`;
    } else {
      audienceTargetAction = `Benchmark the next 3 growth-focused posts against ${growthCompetitorLeader}`;
    }

    bulletPoints.push({
      category: "Audience Health",
      type: "AUDIENCE",
      description: `${brandName}'s audience ${growthVerdict} this period${growthLeaderText}${attribution}.`,
      targetAction: audienceTargetAction,
      icon: growthPct > 0 ? "group_add" : (growthPct < -0.1 ? "person_remove" : "group"),
      color: growthPct > 0 ? "success" : (growthPct < -0.1 ? "error" : "info")
    });

    // Insight 3: Strategy & Efficiency Alert (Volume vs. Quality)
    const primaryPosts = content.totalPublished || 0;
    const isOverPublishing = primaryPosts > marketAvgPosts * 1.5;
    const isUnderPublishing = primaryPosts < marketAvgPosts * 0.5;

    if (isOverPublishing && engagement.avgEngagement < highestCompEngVolume * 0.7) {
      bulletPoints.push({
        category: "Efficiency Alert",
        type: "EFFICIENCY",
        description: `${brandName} is out-publishing the ${platform} market (x1.5 volume), but trailing the resonance leader in return-per-post.`,
        targetAction: "Reduce frequency by 30% and reallocate those resources to high-quality visual production to combat audience fatigue.",
        icon: "warning",
        color: "error"
      });
    } else if (isUnderPublishing && engagement.avgEngagement > highestCompEngVolume * 0.8) {
      const targetVolume = Math.round(marketAvgPosts);
      bulletPoints.push({
        category: "Growth Scalability",
        type: "SCALABILITY",
        description: `${brandName} is achieving superior resonance efficiency on ${platform} despite low volume.`,
        targetAction: `Increase posting frequency to at least ${targetVolume} posts per period to maximize high-resonance creative and capture more 'Discovery' reach.`,
        icon: "trending_up",
        color: "info"
      });
    } else {
      // Market Position (Authority/Discovery)
      const marketAvgEngRate = allProfilesData.reduce((acc, p) => acc + (p.engagement?.rateAvg || 0), 0) / allProfilesData.length;
      
      if (engagement.rateAvg > marketAvgEngRate * 1.5) {
        bulletPoints.push({
          category: "Authority Peak",
          type: "AUTHORITY",
          description: `${brandName} is commanding a disproportionate share of total market engagement on ${platform}.`,
          targetAction: "Leverage this superior brand trust for a direct conversion campaign while competitors struggle with interaction ROI.",
          icon: "military_tech",
          color: "success"
        });
      } else if (engagement.rateAvg < marketAvgEngRate * 0.5) {
         const isLarge = audience.end > 100000;
         const reactivationLabel = isLarge ? "Scale Maintenance" : "Reactivation Opportunity";
         bulletPoints.push({
           category: reactivationLabel,
           type: "REACTIVATION",
           description: `${brandName}'s engagement on ${platform} is lagging relative to its scale.`,
           targetAction: "Launch a community-led campaign (Polls/Q&As) to reactivate dormant followers and improve algorithmic visibility.",
           icon: "rotate_right",
           color: "warning"
         });
      } else if (growthPct < 0.1 && engagement.rateAvg > 5) {
        bulletPoints.push({
          category: "Discovery Bottleneck",
          type: "BOTTLENECK",
          description: "Despite high loyalty, audience growth is stagnant.",
          targetAction: "Shift the next 5 content pieces to 'Shareable' trends or collaborations to break out of the current circle and trigger a new growth spike.",
          icon: "hourglass_empty",
          color: "warning"
        });
      } else {
         bulletPoints.push({
           category: "Engagement Equilibrium",
           type: "EQUILIBRIUM",
           description: `${brandName}'s resonance on ${platform} is tracking consistently with market averages.`,
           targetAction: "Invest in 'Hero' content outliers to break the market tie and move toward an Authority Peak position.",
           icon: "balance",
           color: "info"
         });
      }
    }

  } else {
    // --- Single Brand Strategic Advice ---
    const growthPct = audience.growthPct || 0;
    const engRate = engagement.rateAvg || 0;
    const totalPosts = content.totalPublished || 0;
    const topType = (content.topType || "content").toLowerCase();

    // Growth Advice
    if (growthPct > 1) {
      let attribution = "";
      let targetAction = "Maintain this momentum by increasing frequency to maximize the algorithm's current bias";
      const topPost = content.impactPosts
        ?.filter(p => p.change > 0 && p.relativePerformance > 1.1)
        .sort((a, b) => b.relativePerformance - a.relativePerformance)[0];
      if (topPost) {
        const perfBoost = Math.round((topPost.relativePerformance - 1) * 100);
        const titleContext = topPost.title && topPost.title !== "unnamed content" ? ` ("${topPost.title.substring(0, 30)}...")` : "";
        attribution = ` following ${brandName}'s ${topPost.type} content${titleContext} which saw a ${perfBoost}% higher ROI than the baseline`;
        targetAction = `Scale ${brandName}'s ${topPost.type} production by 25% immediately to capitalize on this resonance window`;
      }
      bulletPoints.push({
        category: "Momentum Window",
        type: "MOMENTUM",
        description: `${brandName}'s audience expanded by ${formatPercent(growthPct, true)}${attribution}.`,
        targetAction: targetAction,
        icon: "rocket_launch",
        color: "success"
      });
    } else if (growthPct < -0.1) {
      let auditTarget = "recent content performance";
      const topDropPost = content.impactPosts?.filter(p => p.change < 0 && p.relativePerformance < 0.9)[0];
      if (topDropPost) {
        const perfDrop = Math.round((1 - topDropPost.relativePerformance) * 100);
        auditTarget = `${brandName}'s ${topDropPost.type} posts around ${dayjs(topDropPost.date).format('MMM D')} which attracted ${perfDrop}% lower engagement than the average`;
      }
      bulletPoints.push({
        category: "Recovery Strategy",
        type: "RECOVERY",
        description: `${brandName}'s audience declined by ${formatPercent(Math.abs(growthPct))}.`,
        targetAction: `Audit ${auditTarget} and pivot 50% of available resources to highest-loyalty formats to stabilize the trend.`,
        icon: "medical_services",
        color: "error"
      });
    } else {
      bulletPoints.push({
        category: "Stability Audit",
        type: "STABILITY",
        description: `${brandName}'s audience size remained stable.`,
        targetAction: "Experiment with 'Share-First' content strategies (e.g. Tips/Tutorials) to trigger new discovery and break the stability plateau.",
        icon: "analytics",
        color: "info"
      });
    }

    // Engagement Advice
    if (engRate > 5) {
      bulletPoints.push({
        category: "Authority Peak",
        type: "AUTHORITY",
        description: `${brandName}'s engagement rate of ${formatPercent(engRate)} is exceptionally high.`,
        targetAction: "This is the ideal window for a direct product launch—leverage this trust-equity before the current resonance cycle cools.",
        icon: "military_tech",
        color: "success"
      });
    } else {
      bulletPoints.push({
        category: "Loyalty Building",
        type: "LOYALTY",
        description: `${brandName}'s engagement rate was ${formatPercent(engRate)}.`,
        targetAction: "Prioritize interactive formats (e.g. Polls/Q&As) to deepen the connection with current subscribers and improve the baseline.",
        icon: "favorite",
        color: "info"
      });
    }

    // Content ROI & Tactical Timing
    if (totalPosts > 0) {
      const isHighlySpecialized = content.typeConcentration > 0.5; // Lowered from 0.8 to 0.5
      const bestDay = content.bestDay;
      const worstDay = content.worstDay;
      
      const capitalizedType = topType.charAt(0).toUpperCase() + topType.slice(1);
      
      let category = "Content ROI Audit";
      let type = "ROI";
      let description = `A total of ${totalPosts} pieces were published for ${brandName}.`;
      let targetAction = `${capitalizedType} formats are ${brandName}'s Highest-Resonance Assets; doubling down on this category will sustain momentum without increasing overall production overhead.`;
      let icon = "fact_check";

      if (isHighlySpecialized && bestDay && bestDay.avg > 0) {
        category = "Tactical Timing Audit";
        type = "TIMING";
        description = `With ${Math.round(content.typeConcentration * 100)}% of content being ${pluralizeFormat(topType)}, ${brandName} has achieved high specialization excellence.`;
        
        const dayDifference = worstDay ? ` (which perform ${Math.round((1 - (worstDay.avg / bestDay.avg)) * 100)}% better than the ${worstDay.day} baseline)` : "";
        targetAction = `Shift primary production or 'Hero' releases to **${bestDay.day}s**${dayDifference} to maximize the organic resonance of the ${topType} format.`;
        icon = "schedule";
      }

      bulletPoints.push({
        category,
        type,
        description,
        targetAction,
        icon,
        color: "info"
      });
    }
  }

  bulletPoints = harmonizeStrategicInsights(bulletPoints);

  return bulletPoints;
};

/**
 * Ensures all generated insights are cohesive and non-contradictory.
 */
const harmonizeStrategicInsights = (bulletPoints) => {
    const audienceInsight = bulletPoints.find(p => p.type === "AUDIENCE" || p.type === "MOMENTUM");
    const efficiencyInsight = bulletPoints.find(p => p.type === "EFFICIENCY");

    if (audienceInsight && efficiencyInsight) {
        // Find the "Growth Driver" (format) from the audience insight
        const match = audienceInsight.targetAction.match(/Double-down on (the )?\*\*(.*?)\*\*/);
        if (match && match[2]) {
            const growthDriver = match[2];
            efficiencyInsight.targetAction = `Reduce total frequency by 30% to combat fatigue—specifically by cutting low-resonance formats—and reallocate those resources into high-performing **${growthDriver}** production.`;
        }
    }
    
    return bulletPoints;
};
